import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';

import { extractAssistantText, extractConversationId } from '../api/pc-chat-response-normalizer';
import { MOCK_CONVERSATIONS } from '../mock/mock-conversations';
import { MOCK_MESSAGES_BY_CONVERSATION_ID } from '../mock/mock-messages';
import { ConversationSearchResponse } from '../search/search.models';
import {
  ChatBackend,
  ConversationListPage,
  ConversationMessagesResponse,
  CreateResponseArgs,
  FeedbackSubmitResult,
} from './chat-backend';
import { ChatMessage, Conversation } from './chat.models';

function nowLabel(): string {
  return 'Now';
}

function makeId(): string {
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function truncateTitle(s: string): string {
  const t = s.trim().replace(/\s+/g, ' ');
  if (!t) return 'New chat';
  return t;
}

function toPromptText(input: unknown): string {
  if (typeof input === 'string') return input;
  if (
    input &&
    typeof input === 'object' &&
    'text' in input &&
    typeof (input as Record<string, unknown>)['text'] === 'string'
  ) {
    return (input as Record<string, unknown>)['text'] as string;
  }
  return '';
}

@Injectable({ providedIn: 'root' })
export class MockChatBackendService implements ChatBackend {
  private conversations: Conversation[] = [...MOCK_CONVERSATIONS];
  private messagesByConversationId: Record<string, ChatMessage[]> = {
    ...MOCK_MESSAGES_BY_CONVERSATION_ID,
  };

  listConversations(page = 1, limit = 20): Observable<ConversationListPage> {
    const total_count = this.conversations.length;
    const total_pages = Math.max(1, Math.ceil(total_count / limit));
    const safePage = Math.max(1, Math.min(page, total_pages));
    const startIdx = (safePage - 1) * limit;
    const endIdx = startIdx + limit;
    const sliced = this.conversations.slice(startIdx, endIdx);

    return of({
      conversations: sliced,
      pagination: {
        page: safePage,
        limit,
        total_pages,
        total_count,
      },
    });
  }

  searchConversations(query: string, limit = 20): Observable<ConversationSearchResponse> {
    const normalized = (query ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    if (!normalized) return of({ results: [], query: '', totalCount: 0 });

    const matches = this.conversations.filter((c) =>
      (c.title ?? '').toLowerCase().includes(normalized),
    );
    const limited = matches.slice(0, Math.max(0, limit));

    return of({
      query: query.trim().replace(/\s+/g, ' '),
      totalCount: matches.length,
      results: limited.map((c) => ({
        conversationId: c.id,
        title: c.title,
        snippet: c.title,
        matchType: 'title',
        createdAtLabel: c.updatedAtLabel,
      })),
    });
  }

  listMessages(conversationId: string): Observable<ConversationMessagesResponse> {
    const convo = this.conversations.find((c) => c.id === conversationId);
    return of({
      messages: [...(this.messagesByConversationId[conversationId] ?? [])],
      status: convo?.status ?? null,
      deepResearch: false,
      pagination: {
        page: 1,
        limit: 100,
        total_pages: 1,
        total_count: (this.messagesByConversationId[conversationId] ?? []).length,
      },
    });
  }

  renameConversation(conversationId: string, title: string): Observable<void> {
    const normalized = title.trim().replace(/\s+/g, ' ');
    if (!normalized) return of(void 0);

    const idx = this.conversations.findIndex((c) => c.id === conversationId);
    if (idx !== -1) this.conversations[idx] = { ...this.conversations[idx], title: normalized };

    return of(void 0);
  }

  deleteConversation(conversationId: string): Observable<void> {
    this.conversations = this.conversations.filter((c) => c.id !== conversationId);
    const next = { ...this.messagesByConversationId };
    delete next[conversationId];
    this.messagesByConversationId = next;
    return of(void 0);
  }

  submitFeedback(
    conversationId: string,
    rating: string,
    comment = '',
  ): Observable<FeedbackSubmitResult> {
    const normalizedComment = (comment ?? '').trim() ? comment : null;
    const normalizedRating = rating === 'positive' || rating === 'negative' ? rating : null;

    const idx = this.conversations.findIndex((c) => c.id === conversationId);
    if (idx !== -1) {
      this.conversations[idx] = { ...this.conversations[idx], feedbackRating: normalizedRating };
    }

    return of({
      feedbackRating: normalizedRating,
      feedbackComment: normalizedComment,
      feedbackAt: new Date().toISOString(),
    });
  }

  stopConversation(): Observable<void> {
    return of(void 0);
  }

  createResponse(args: CreateResponseArgs): Observable<unknown> {
    const prompt = toPromptText(args.input).trim();
    const nextConversationId = args.conversationId ?? makeId();
    const simulatedStreamDelayMs = prompt.startsWith('/slow') ? 1200 : 30;

    // Keep parity with API surface; mock ignores these fields for now.
    void args.model;
    void args.deepResearch;
    void args.retry;
    void args.edited;

    if (!this.conversations.some((c) => c.id === nextConversationId)) {
      const convo: Conversation = {
        id: nextConversationId,
        title: truncateTitle(prompt),
        updatedAtLabel: nowLabel(),
      };
      this.conversations = [convo, ...this.conversations];
      this.messagesByConversationId = {
        ...this.messagesByConversationId,
        [nextConversationId]: [],
      };
    } else {
      // Move to top and update "Now"
      const idx = this.conversations.findIndex((c) => c.id === nextConversationId);
      if (idx !== -1) {
        const updated: Conversation = { ...this.conversations[idx], updatedAtLabel: nowLabel() };
        this.conversations = [
          updated,
          ...this.conversations.slice(0, idx),
          ...this.conversations.slice(idx + 1),
        ];
      }
    }

    const userMsg: ChatMessage = { id: makeId(), role: 'user', content: prompt };

    if (prompt === '/linktest') {
      const assistantMsg: ChatMessage = {
        id: makeId(),
        role: 'assistant',
        label: 'Analysis',
        contentMarkdown:
          'Link test:\n\n- Markdown link: [Go to Search](/search?data=mock)\n- Raw HTML: <a href="/search?data=mock">Go to Search (HTML)</a>\n',
      };

      const prev = this.messagesByConversationId[nextConversationId] ?? [];
      this.messagesByConversationId = {
        ...this.messagesByConversationId,
        [nextConversationId]: [...prev, userMsg, assistantMsg],
      };

      const payload = {
        conversation_id: nextConversationId,
        output_text: assistantMsg.contentMarkdown,
      };

      void extractConversationId(payload);
      void extractAssistantText(payload);
      // Simulate a brief stream delay so UI behaviors that depend on an in-progress placeholder are testable.
      return args.stream ? of(payload).pipe(delay(simulatedStreamDelayMs)) : of(payload);
    }

    const assistantMsg: ChatMessage = {
      id: makeId(),
      role: 'assistant',
      label: 'Analysis',
      contentMarkdown: `Hi! You said: **${prompt.replace(/[*_`]/g, '')}**`,
    };

    const prev = this.messagesByConversationId[nextConversationId] ?? [];
    this.messagesByConversationId = {
      ...this.messagesByConversationId,
      [nextConversationId]: [...prev, userMsg, assistantMsg],
    };

    const payload = {
      conversation_id: nextConversationId,
      output_text: assistantMsg.contentMarkdown,
    };

    // Keep a consistent surface area with ApiChatBackend which might emit varying shapes.
    void extractConversationId(payload);
    void extractAssistantText(payload);

    // Simulate a brief stream delay so UI behaviors that depend on an in-progress placeholder are testable.
    return args.stream ? of(payload).pipe(delay(simulatedStreamDelayMs)) : of(payload);
  }
}
